import { FormikErrors } from 'formik';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { FormSection } from '@@/form-components/FormSection';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Slider } from '@@/form-components/Slider';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';
import { SliderWithInput } from '@@/form-components/Slider/SliderWithInput';

import { useClusterResourceLimitsQuery } from '../../../queries/useResourceLimitsQuery';

import { NamespaceResourceReservation } from './NamespaceResourceReservation';
import { ResourceQuotaFormValues } from './types';

interface Props {
  values: ResourceQuotaFormValues;
  onChange: (value: ResourceQuotaFormValues) => void;
  enableResourceOverCommit?: boolean;
  errors?: FormikErrors<ResourceQuotaFormValues>;
  namespaceName?: string;
  isEdit?: boolean;
  isEditingDisabled?: boolean;
}

export function ResourceQuotaFormSection({
  values,
  onChange,
  errors,
  isEdit,
  enableResourceOverCommit,
  namespaceName,
  isEditingDisabled,
}: Props) {
  const environmentId = useEnvironmentId();
  const resourceLimitsQuery = useClusterResourceLimitsQuery(environmentId);
  const cpuLimit = resourceLimitsQuery.data?.CPU ?? 0;
  const memoryLimit = resourceLimitsQuery.data?.Memory ?? 0;

  /**
   * Generic handler for resource request changes
   * @param field - The field to update ('memoryRequest' or 'cpuRequest')
   * @param limitField - The corresponding limit field ('memoryLimit' or 'cpuLimit')
   * @param value - The new value
   */
  function handleRequestChange(
    requestField: 'memoryRequest' | 'cpuRequest',
    limitField: 'memoryLimit' | 'cpuLimit',
    value: number
  ) {
    const newValue = value.toString();
    const currentLimit = Number(values[limitField]) || 0;

    // If the new request is greater than the current limit,
    // update the limit to match the request
    if (value > currentLimit) {
      onChange({
        ...values,
        [requestField]: newValue,
        [limitField]: newValue,
      });
    } else {
      onChange({
        ...values,
        [requestField]: newValue,
      });
    }
  }

  /**
   * Generic handler for resource limit changes
   * @param field - The field to update ('memoryLimit' or 'cpuLimit')
   * @param requestField - The corresponding request field ('memoryRequest' or 'cpuRequest')
   * @param value - The new value
   */
  function handleLimitChange(
    limitField: 'memoryLimit' | 'cpuLimit',
    requestField: 'memoryRequest' | 'cpuRequest',
    value: number
  ) {
    const newValue = value.toString();
    const currentRequest = Number(values[requestField]) || 0;

    // If the new limit is less than the current request,
    // update the request to match the limit
    if (value < currentRequest) {
      onChange({
        ...values,
        [limitField]: newValue,
        [requestField]: newValue,
      });
    } else {
      onChange({
        ...values,
        [limitField]: newValue,
      });
    }
  }

  // Specific handlers for each slider
  function handleMemoryRequestChange(value: number) {
    handleRequestChange('memoryRequest', 'memoryLimit', value);
  }

  function handleMemoryLimitChange(value: number) {
    handleLimitChange('memoryLimit', 'memoryRequest', value);
  }

  function handleCpuRequestChange(cpu: number | number[]) {
    if (Array.isArray(cpu)) {
      return;
    }
    handleRequestChange('cpuRequest', 'cpuLimit', cpu);
  }

  function handleCpuLimitChange(cpu: number | number[]) {
    if (Array.isArray(cpu)) {
      return;
    }
    handleLimitChange('cpuLimit', 'cpuRequest', cpu);
  }

  return (
    <FormSection title="Resource quota">
      {!isEditingDisabled && (
        <>
          <TextTip color="blue" className="mb-2">
            A resource quota sets boundaries on the compute resources a
            namespace can use. It&apos;s good practice to set a quota for a
            namespace to manage resources effectively. Alternatively, you can
            disable assigning a quota for unrestricted access (not recommended).
          </TextTip>

          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                data-cy="k8sNamespaceCreate-resourceAssignmentToggle"
                disabled={!enableResourceOverCommit}
                label="Resource assignment"
                labelClass="col-sm-3 col-lg-2"
                checked={values.enabled || !enableResourceOverCommit}
                onChange={(enabled) => onChange({ ...values, enabled })}
              />
            </div>
          </div>
        </>
      )}

      {(values.enabled || !enableResourceOverCommit) && !isEditingDisabled && (
        <div>
          <FormSectionTitle>Set resource limits</FormSectionTitle>
          {(!cpuLimit || !memoryLimit) && (
            <FormError>
              Not enough resources available in the cluster to apply a resource
              reservation.
            </FormError>
          )}

          <FormControl
            label="Memory request (MB)"
            inputId="memory-request"
            className="[&>label]:mt-8"
            errors={errors?.memoryRequest}
          >
            {memoryLimit >= 0 && (
              <SliderWithInput
                value={Number(values.memoryRequest) ?? 0}
                onChange={handleMemoryRequestChange}
                max={memoryLimit}
                min={0}
                step={128}
                dataCy="k8sNamespaceCreate-memoryRequest"
                visibleTooltip
                inputId="memory-request"
              />
            )}
          </FormControl>

          <FormControl
            label="Memory limit (MB)"
            inputId="memory-limit"
            className="[&>label]:mt-8"
            errors={errors?.memoryLimit}
          >
            {memoryLimit >= 0 && (
              <SliderWithInput
                value={Number(values.memoryLimit) ?? 0}
                onChange={handleMemoryLimitChange}
                max={memoryLimit}
                min={0}
                step={128}
                dataCy="k8sNamespaceCreate-memoryLimit"
                visibleTooltip
                inputId="memory-limit"
              />
            )}
          </FormControl>

          <FormControl
            label="CPU request"
            inputId="cpu-request"
            className="[&>label]:mt-8"
            errors={errors?.cpuRequest}
          >
            {cpuLimit >= 0 && (
              <Slider
                min={0}
                max={cpuLimit / 1000}
                step={0.1}
                value={Number(values.cpuRequest) ?? 0}
                onChange={handleCpuRequestChange}
                dataCy="k8sNamespaceCreate-cpuRequestSlider"
                visibleTooltip
              />
            )}
          </FormControl>

          <FormControl
            label="CPU limit"
            inputId="cpu-limit"
            className="[&>label]:mt-8"
            errors={errors?.cpuLimit}
          >
            {cpuLimit >= 0 && (
              <Slider
                min={0}
                max={cpuLimit / 1000}
                step={0.1}
                value={Number(values.cpuLimit) ?? 0}
                onChange={handleCpuLimitChange}
                dataCy="k8sNamespaceCreate-cpuLimitSlider"
                visibleTooltip
              />
            )}
          </FormControl>

          {cpuLimit && memoryLimit && typeof errors === 'string' ? (
            <FormError>{errors}</FormError>
          ) : null}
        </div>
      )}
      {namespaceName && isEdit && (
        <NamespaceResourceReservation
          namespaceName={namespaceName}
          environmentId={environmentId}
          resourceQuotaValues={values}
        />
      )}
    </FormSection>
  );
}
